import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { UserEntity } from '@backstage/catalog-model';

/** Maximum chain depth before we stop walking. Safety net against runaway
 *  recursion on unexpectedly deep trees; also bounds the request count.
 *  Six covers typical org hierarchies (IC → EM → Director → VP → SVP →
 *  C-suite). */
const MAX_CHAIN_DEPTH = 6;

export interface ManagerChainEntry {
  ref: string;
  displayName: string;
  role?: string;
}

export interface UseManagerChainResult {
  /** Resolved entries in top-down order (most-senior first). */
  chain: ManagerChainEntry[];
  /** True when the walk stopped early — either because MAX_CHAIN_DEPTH was
   *  hit or a cycle was detected. UI uses this to render a "…" indicator. */
  truncated: boolean;
}

/**
 * Walks the `user/manager` annotation upward from a starting ref, returning
 * the resolved entries in top-down order plus a flag indicating whether the
 * walk was truncated.
 *
 * Encapsulates the async walking loop, cycle detection, depth cap, state
 * reset on prop change, and cancellation guard — separating the data-fetch
 * concern from the rendering concern in EntityUserManagerChain.
 *
 * Each hop is one `catalogApi.getEntityByRef` call; total requests are
 * bounded by MAX_CHAIN_DEPTH.
 */
export function useManagerChain(
  initialManagerRef: string | undefined,
): UseManagerChainResult {
  const catalogApi = useApi(catalogApiRef);
  const [chain, setChain] = useState<ManagerChainEntry[]>([]);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    // Reset on prop change so a navigation between users doesn't briefly
    // show the previous user's chain.
    setChain([]);
    setTruncated(false);
    if (!initialManagerRef) return undefined;

    let cancelled = false;

    (async () => {
      const visited = new Set<string>();
      const collected: ManagerChainEntry[] = [];
      let currentRef: string | undefined = initialManagerRef;
      let depth = 0;
      let didTruncate = false;

      while (currentRef && depth < MAX_CHAIN_DEPTH) {
        // Cycle guard — broken data could loop forever otherwise.
        if (visited.has(currentRef)) {
          didTruncate = true;
          break;
        }
        visited.add(currentRef);
        let entity;
        try {
          entity = await catalogApi.getEntityByRef(currentRef);
        } catch {
          break;
        }
        if (cancelled) return;
        if (!entity) break;
        const u = entity as UserEntity;
        collected.push({
          ref: currentRef,
          displayName: u.spec?.profile?.displayName ?? u.metadata.name,
          role: u.metadata.annotations?.['user/role'],
        });
        currentRef = u.metadata.annotations?.['user/manager'];
        depth += 1;
      }

      // If we hit the depth cap and there's still more chain to walk, flag
      // the result as truncated.
      if (currentRef && depth >= MAX_CHAIN_DEPTH) {
        didTruncate = true;
      }

      if (!cancelled) {
        // Render top-down (most-senior leftmost) — reverse the walked list.
        setChain(collected.reverse());
        setTruncated(didTruncate);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [catalogApi, initialManagerRef]);

  return { chain, truncated };
}
